import React, { createContext, useContext, useState } from "react";

/**
 * 顶栏右侧 / 左侧（替代标题）：各拆 setter / content 两个 Context，避免「只调用 setHeader* 的页面」在顶栏内容更新时整页重渲染。
 *
 * 若合并为单一 { headerRight, setHeaderRight }，则每次 setHeaderRight 后 value 引用变化，
 * 所有 useContext 消费者（含 StoreRevenueSingle）都会重渲染；其 useLayoutEffect 依赖若再次触发
 * setHeaderRight，会形成「Maximum update depth exceeded」(React #185)。
 */
const LayoutHeaderRightSetterContext = createContext<
  ((node: React.ReactNode) => void) | null
>(null);

const LayoutHeaderRightContentContext = createContext<React.ReactNode>(null);

/** 顶栏左侧（替代默认标题）：同样拆 setter / content，避免页面只注册 setter 却因内容更新整页重渲染 */
const LayoutHeaderLeftSetterContext = createContext<
  ((node: React.ReactNode) => void) | null
>(null);
const LayoutHeaderLeftContentContext = createContext<React.ReactNode>(null);

export const LayoutShellProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [headerRight, setHeaderRight] = useState<React.ReactNode>(null);
  const [headerLeft, setHeaderLeft] = useState<React.ReactNode>(null);

  /** useState 的 setter 引用稳定；与 headerRight 分 Context，避免 setHeaderRight 触发整树重渲染 */
  return (
    <LayoutHeaderRightSetterContext.Provider value={setHeaderRight}>
      <LayoutHeaderLeftSetterContext.Provider value={setHeaderLeft}>
        <LayoutHeaderRightContentContext.Provider value={headerRight}>
          <LayoutHeaderLeftContentContext.Provider value={headerLeft}>
            {children}
          </LayoutHeaderLeftContentContext.Provider>
        </LayoutHeaderRightContentContext.Provider>
      </LayoutHeaderLeftSetterContext.Provider>
    </LayoutHeaderRightSetterContext.Provider>
  );
};

export type LayoutShellContextValue = {
  headerRight: React.ReactNode;
  headerLeft: React.ReactNode;
  setHeaderRight: (node: React.ReactNode) => void;
  setHeaderLeft: (node: React.ReactNode) => void;
};

export const useLayoutShell = (): Pick<
  LayoutShellContextValue,
  "headerRight" | "headerLeft"
> => {
  const setHeaderRight = useContext(LayoutHeaderRightSetterContext);
  if (setHeaderRight == null) {
    throw new Error("useLayoutShell must be used within LayoutShellProvider");
  }
  const headerRight = useContext(LayoutHeaderRightContentContext);
  const headerLeft = useContext(LayoutHeaderLeftContentContext);
  return { headerRight, headerLeft };
};

/** 仅注册顶栏右侧；只订阅 setter Context，顶栏内容变化不会触发本 hook 所在组件重渲染 */
export const useLayoutHeaderRight = (): LayoutShellContextValue["setHeaderRight"] => {
  return useContext(LayoutHeaderRightSetterContext) ?? (() => {});
};

/** 仅注册顶栏左侧（替代标题）；只订阅 setter Context */
export const useLayoutHeaderLeft = (): LayoutShellContextValue["setHeaderLeft"] => {
  return useContext(LayoutHeaderLeftSetterContext) ?? (() => {});
};
